(() => {
  if (window.RareTCGFacetsLoaded) return;
  window.RareTCGFacetsLoaded = true;

  const ROOT_SELECTOR = '[data-rtcg-facets]';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  let pendingState = null;
  let drawerOpener = null;
  let viewMode = 'grid';
  try {
    if (window.sessionStorage.getItem('rtcg:mobile-view') === 'list') viewMode = 'list';
  } catch (_) { /* Browsing remains usable if storage is blocked. */ }

  const syncViewMode = () => {
    document.body.classList.toggle('rtcg-mobile-list', viewMode === 'list');
    document.querySelectorAll('[data-rtcg-view-toggle] [data-rtcg-view]').forEach((button) => {
      const active = button.dataset.rtcgView === viewMode;
      button.setAttribute('aria-pressed', String(active));
    });
  };

  const selectViewMode = (mode) => {
    if (mode !== 'grid' && mode !== 'list') return;
    viewMode = mode;
    try { window.sessionStorage.setItem('rtcg:mobile-view', mode); } catch (_) {}
    syncViewMode();
  };


  const facetRoots = (root = document) => {
    const roots = [];
    if (root instanceof Element && root.matches(ROOT_SELECTOR)) roots.push(root);
    if (root.querySelectorAll) roots.push(...root.querySelectorAll(ROOT_SELECTOR));
    return roots;
  };

  const activeRoot = () => document.querySelector(ROOT_SELECTOR);

  const captureState = (event) => {
    const root = activeRoot();
    if (!root) return;
    const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const drawer = root.querySelector('[data-rtcg-drawer]');
    const drawerScroll = drawer?.querySelector('[data-rtcg-drawer-scroll]');
    pendingState = {
      open: [...root.querySelectorAll('.rtcg-facet-group[open]')].map((item) => `${item.closest('#FacetFiltersFormMobile') ? 'mobile' : 'desktop'}:${item.dataset.filterKey}`),
      expanded: [...root.querySelectorAll('[data-rtcg-facet-show-more][aria-expanded="true"]')].map((button) => `${button.closest('#FacetFiltersFormMobile') ? 'mobile' : 'desktop'}:${button.closest('.rtcg-facet-group')?.dataset.filterKey || ''}`),
      searches: [...root.querySelectorAll('[data-rtcg-facet-search]')]
        .filter((input) => input.value.trim())
        .map((input) => ({
          scope: input.closest('#FacetFiltersFormMobile') ? 'mobile' : 'desktop',
          key: input.closest('.rtcg-facet-group')?.dataset.filterKey || '',
          query: input.value,
        })),
      focus: focused ? { id: focused.id, name: focused.getAttribute('name'), value: focused.value } : null,
      pageY: window.scrollY,
      drawerOpen: drawer?.classList.contains('is-open') || false,
      drawerY: drawerScroll?.scrollTop || 0,
      openerId: drawerOpener?.id || '',
      preservePage: !event?.target?.closest?.('.pagination a'),
    };
  };

  const restoreState = (root) => {
    if (!pendingState) return;
    const state = pendingState;
    root.querySelectorAll('.rtcg-facet-group').forEach((group) => {
      const scope = group.closest('#FacetFiltersFormMobile') ? 'mobile' : 'desktop';
      const active = group.dataset.filterActive === 'true';
      group.open = active || state.open.includes(`${scope}:${group.dataset.filterKey}`);
      const more = group.querySelector('[data-rtcg-facet-show-more]');
      if (more) setOverflowExpanded(group, state.expanded?.includes(`${scope}:${group.dataset.filterKey}`) || false);
      const savedSearch = state.searches?.find((entry) => entry.scope === scope && entry.key === group.dataset.filterKey);
      const input = group.querySelector('[data-rtcg-facet-search]');
      if (savedSearch && input) {
        input.value = savedSearch.query;
        optionSearch(input);
      }
    });
    const drawerScroll = root.querySelector('[data-rtcg-drawer-scroll]');
    if (state.drawerOpen) openDrawer(root, state.openerId ? document.getElementById(state.openerId) : null, false);

    requestAnimationFrame(() => {
      // MOB-104: restore drawer scroll only after the replaced drawer is visible.
      // Hidden drawer elements cannot reliably retain scrollTop on iOS Safari.
      if (state.drawerOpen && drawerScroll?.isConnected) drawerScroll.scrollTop = state.drawerY;
      if (state.preservePage) window.scrollTo({ top: state.pageY, left: 0, behavior: 'auto' });
      if (state.focus) {
        let control = state.focus.id ? document.getElementById(state.focus.id) : null;
        if (control && state.focus.name && (control.getAttribute('name') !== state.focus.name || control.value !== state.focus.value)) control = null;
        if (!control && state.focus.name) {
          control = [...root.querySelectorAll(`[name="${CSS.escape(state.focus.name)}"]`)].find((candidate) => candidate.value === state.focus.value);
        }
        control?.focus({ preventScroll: true });
      }
      pendingState = null;
    });
  };

  const setOverflowExpanded = (group, expanded) => {
    const button = group?.querySelector('[data-rtcg-facet-show-more]');
    if (!button) return;
    group.dataset.rtcgOverflowExpanded = expanded ? 'true' : 'false';
    button.setAttribute('aria-expanded', String(expanded));
    button.textContent = expanded ? button.dataset.expandedLabel : button.dataset.collapsedLabel;
    group.querySelectorAll('[data-rtcg-facet-overflow]').forEach((option) => {
      option.hidden = !expanded;
    });
  };

  const optionSearch = (input) => {
    const group = input.closest('.rtcg-facet-group');
    const query = input.value.trim().toLocaleLowerCase();
    const more = group?.querySelector('[data-rtcg-facet-show-more]');
    let visible = 0;
    group?.querySelectorAll('[data-rtcg-facet-option]').forEach((option) => {
      const matches = !query || (option.dataset.searchValue || '').includes(query);
      if (query) {
        option.hidden = !matches;
      } else {
        const overflow = option.hasAttribute('data-rtcg-facet-overflow');
        const expanded = group.dataset.rtcgOverflowExpanded === 'true';
        option.hidden = overflow && !expanded;
      }
      if (matches && !option.hidden) visible += 1;
    });
    if (more) more.hidden = Boolean(query);
    const empty = group?.querySelector('[data-rtcg-facet-search-empty]');
    if (empty) empty.hidden = visible !== 0;
  };

  const syncSortControl = (select) => {
    const wrap = select.closest('.rtcg-sort__select-wrap') || select.parentElement;
    const custom = wrap?.nextElementSibling;
    if (!custom?.matches('.rtcg-custom-sort')) return;

    const selectedText = select.options[select.selectedIndex]?.textContent.trim() || '';
    const mobileLabel = select.closest('.rtcg-sort--mobile') ? 'Sort: ' : '';
    const value = custom.querySelector('.rtcg-custom-sort__value');
    if (value) value.textContent = `${mobileLabel}${selectedText}`;
    custom.querySelector('.rtcg-custom-sort__button')?.setAttribute('aria-label', `Sort products: ${selectedText}`);
    custom.querySelectorAll('.rtcg-custom-sort__option').forEach((item) => {
      const selected = item.dataset.value === select.value;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-selected', String(selected));
    });
  };

  const buildSort = (select) => {
    if (select.dataset.rtcgEnhanced === 'true') return;
    select.dataset.rtcgEnhanced = 'true';
    const wrap = select.closest('.rtcg-sort__select-wrap') || select.parentElement;
    wrap?.classList.add('is-enhanced');
    const custom = document.createElement('div');
    custom.className = 'rtcg-custom-sort';
    const button = document.createElement('button');
    button.type = 'button';
    button.id = `${select.id}-Custom`;
    button.className = 'rtcg-custom-sort__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    const selectedText = select.options[select.selectedIndex]?.textContent.trim() || '';
    // MOB-402: include the active order in the button's accessible name.
    button.setAttribute('aria-label', `Sort products: ${selectedText}`);
    const mobileLabel = select.closest('.rtcg-sort--mobile') ? 'Sort: ' : '';
    const selectedValue = document.createElement('span');
    selectedValue.className = 'rtcg-custom-sort__value';
    selectedValue.textContent = `${mobileLabel}${selectedText}`;
    const caret = document.createElement('span');
    caret.className = 'rtcg-custom-sort__caret';
    caret.setAttribute('aria-hidden', 'true');
    button.append(selectedValue, caret);
    const menu = document.createElement('div');
    menu.className = 'rtcg-custom-sort__menu';
    menu.id = `${select.id}-Menu`;
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', 'Sort products');
    button.setAttribute('aria-controls', menu.id);

    [...select.options].forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = `rtcg-custom-sort__option${option.selected ? ' is-selected' : ''}`;
      item.dataset.value = option.value;
      item.setAttribute('role', 'option');
      item.tabIndex = -1;
      item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
      item.textContent = option.textContent.trim();
      item.addEventListener('click', () => {
        select.value = option.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        closeSort(custom);
        button.focus({ preventScroll: true });
      });
      menu.appendChild(item);
    });

    button.addEventListener('click', () => {
      const open = !custom.classList.contains('is-open');
      document.querySelectorAll('.rtcg-custom-sort.is-open').forEach(closeSort);
      custom.classList.toggle('is-open', open);
      button.setAttribute('aria-expanded', String(open));
      if (open) menu.querySelector('.is-selected, button')?.focus({ preventScroll: true });
    });
    menu.addEventListener('keydown', (event) => sortKeydown(event, custom, button));
    custom.append(button, menu);
    wrap?.after(custom);
    select.addEventListener('input', () => syncSortControl(select));
  };

  const closeSort = (custom) => {
    custom.classList.remove('is-open');
    custom.querySelector('.rtcg-custom-sort__button')?.setAttribute('aria-expanded', 'false');
  };

  const sortKeydown = (event, custom, button) => {
    const items = [...custom.querySelectorAll('.rtcg-custom-sort__option')];
    const index = items.indexOf(document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeSort(custom);
      button.focus({ preventScroll: true });
    }
    if (event.key === 'Tab') closeSort(custom);
    if (event.key === 'Home') { event.preventDefault(); items[0]?.focus(); }
    if (event.key === 'End') { event.preventDefault(); items.at(-1)?.focus(); }
    if (event.key === 'ArrowDown') { event.preventDefault(); (items[index + 1] || items[0])?.focus(); }
    if (event.key === 'ArrowUp') { event.preventDefault(); (items[index - 1] || items.at(-1))?.focus(); }
  };

  const openDrawer = (root, opener = null, focus = true) => {
    const drawer = root.querySelector('[data-rtcg-drawer]');
    if (!drawer) return;
    // MOB-104: AJAX section replacement invalidates the previous opener element.
    drawerOpener = opener?.isConnected ? opener
      : root.querySelector('[data-rtcg-drawer-open]') || (drawerOpener?.isConnected ? drawerOpener : null);
    drawer.classList.add('is-open');
    if (!pendingState && drawer.dataset.rtcgInitialExpansion !== 'true') {
      const groups = [...drawer.querySelectorAll('#FacetFiltersFormMobile .rtcg-facet-group')];
      const available = groups.find((group) => (group.dataset.filterKey || '').toLowerCase().includes('availability'));
      const productType = groups.find((group) => (group.dataset.filterKey || '').toLowerCase().includes('product_type'));
      if (productType) productType.open = true;
      if (available) available.open = true;
      if (!productType && !available && groups.length) groups[0].open = true;
      drawer.dataset.rtcgInitialExpansion = 'true';
    }
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    root.querySelector('[data-rtcg-drawer-open]')?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('rtcg-facets-drawer-open');
    if (focus) requestAnimationFrame(() => drawer.querySelector('.rtcg-facet-drawer__close')?.focus({ preventScroll: true }));
  };

  const closeDrawer = (root, returnFocus = true) => {
    const drawer = root.querySelector('[data-rtcg-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    root.querySelector('[data-rtcg-drawer-open]')?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('rtcg-facets-drawer-open');
    if (returnFocus) drawerOpener?.focus({ preventScroll: true });
  };

  const drawerKeydown = (event, root) => {
    const drawer = root.querySelector('[data-rtcg-drawer].is-open');
    if (!drawer) return;
    if (event.key === 'Escape') { event.preventDefault(); closeDrawer(root); return; }
    if (event.key !== 'Tab') return;
    const controls = [...drawer.querySelectorAll(FOCUSABLE)].filter((item) => item.getClientRects().length);
    if (!controls.length) return;
    const first = controls[0];
    const last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  const initRoot = (root) => {
    if (root.dataset.rtcgBound !== 'true') {
      root.dataset.rtcgBound = 'true';
      root.addEventListener('input', (event) => {
        if (event.target.matches('[data-rtcg-facet-search]')) optionSearch(event.target);
      }, true);
      root.addEventListener('click', (event) => {
        const viewButton = event.target.closest('[data-rtcg-view]');
        if (viewButton) {
          event.preventDefault();
          selectViewMode(viewButton.dataset.rtcgView);
          return;
        }
        const more = event.target.closest('[data-rtcg-facet-show-more]');
        if (more) {
          event.preventDefault();
          const group = more.closest('.rtcg-facet-group');
          if (group) {
            const expanded = more.getAttribute('aria-expanded') === 'true';
            setOverflowExpanded(group, !expanded);
            // A facet-value search temporarily hides the expansion control;
            // keep its query authoritative if the two states ever overlap.
            const input = group.querySelector('[data-rtcg-facet-search]');
            if (input?.value.trim()) optionSearch(input);
          }
          return;
        }
        const open = event.target.closest('[data-rtcg-drawer-open]');
        const close = event.target.closest('[data-rtcg-drawer-close]');
        if (open) openDrawer(root, open);
        if (close) closeDrawer(root);
      });
      root.addEventListener('keydown', (event) => drawerKeydown(event, root));
    }
    const drawer = root.querySelector('[data-rtcg-drawer]');
    if (drawer && !drawer.classList.contains('is-open')) {
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      drawer.setAttribute('aria-hidden', 'true');
    }
    root.querySelectorAll('.rtcg-sort__select').forEach(buildSort);
    root.querySelectorAll('.rtcg-facet-group[data-filter-active="true"]').forEach((group) => { group.open = true; });
    restoreState(root);
    syncViewMode();
  };

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-rtcg-facet-search]')) {
      optionSearch(event.target);
      event.stopPropagation();
    }
  }, true);
  document.addEventListener('rtcg:collection-updating', captureState);
  document.addEventListener('rtcg:collection-updated', () => facetRoots().forEach(initRoot));
  document.addEventListener('rtcg:collection-update-failed', () => {
    facetRoots().forEach((root) => root.querySelectorAll('.rtcg-sort__select').forEach(syncSortControl));
    pendingState = null;
  });
  document.addEventListener('shopify:section:load', (event) => facetRoots(event.target).forEach(initRoot));
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.rtcg-custom-sort')) document.querySelectorAll('.rtcg-custom-sort.is-open').forEach(closeSort);
  });
  document.addEventListener('DOMContentLoaded', () => {
    facetRoots().forEach(initRoot);
  });
})();
